import * as React from "react";
import {
  Outlet,
  createRootRouteWithContext,
  useRouterState,
} from "@tanstack/react-router";
import type { RouterContext } from "@/shared/lib/router-context";
import { RootProvider } from "@/app/providers/root-provider";
import { HubSpotTracking } from "@/features/hubspot-chat";
import { SeatLimitDialog } from "@/features/seat-limit-dialog";
import { UpgradeDialog } from "@/features/upgrade-dialog";
import { ExternalLinkDialog } from "@/shared/ui/external-link-dialog";
import { Toaster } from "@/shared/ui/sonner";
import { csrfRequest, getXsrfToken } from "@/shared/lib/csrf";
import { fetchAuthenticatedUser } from "@/entities/me/api/fetch-authenticated-user";
import { getCachedAuth, setCachedAuth } from "@/shared/lib/auth";
import { fetchTranslations } from "@/shared/i18n";
import { getCurrentLang } from "@/features/language";

// Full-page white flows that should not show the app background gradient.
const PLAIN_BG_PATH = /^\/auth|^\/onboarding|\/subscribe(\/|$)/;
// Admin routes get their own tinted background. Anchored to the `/admin`
// section segment (routes are `/$companySlug/admin/...`) so a company whose
// slug is literally "admin" doesn't tint every page.
const ADMIN_BG_PATH = /^\/[^/]+\/admin(\/|$)/;

const RootComponent = () => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const plainBg = PLAIN_BG_PATH.test(pathname);
  const adminBg = ADMIN_BG_PATH.test(pathname);

  React.useLayoutEffect(() => {
    document.documentElement.classList.toggle("plain-bg", plainBg);
    document.documentElement.classList.toggle("admin-bg", adminBg);
  }, [plainBg, adminBg]);

  return (
    <>
      <React.StrictMode>
        <RootProvider>
          <HubSpotTracking />
          <Outlet />
          <UpgradeDialog />
          <SeatLimitDialog />
          <ExternalLinkDialog />
          <Toaster />
        </RootProvider>
      </React.StrictMode>
    </>
  );
};

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  beforeLoad: async () => {
    // Prefetch translations so they're cached by the time TranslationProvider mounts
    void fetchTranslations(getCurrentLang());

    const cached = getCachedAuth();
    if (cached) return { me: cached };

    if (!getXsrfToken()) await csrfRequest();
    const me = await fetchAuthenticatedUser().catch(() => null);
    setCachedAuth(me);
    return { me };
  },
  staleTime: Infinity,
});
